import { Box, IconButton, InputAdornment, Modal, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Slide } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getIcon } from "utils/iconsConfig";
import QuantityCard from "../QuanitityCard/QuanitiyCard";
import * as XLSX from "xlsx";
import apiClient from "lib/api-client";
import { addToCart, clearCart, removeFromCart } from "store/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import { transformDataForCreateOrderPage, transformStockData } from "utils/transformdata";
import StockCard from "components/ui/StockCard";
import "features/Stock/StockProd/components/product.css";

const modalStyle = {
  position: "absolute",
  top: 0,
  right: 0,
  height: "100vh",
  width: "450px",
  background: "#E9E9ED",
  borderRadius: "6px 0 0 6px",
  padding: "14px 12px 16px",
  display: "flex",
  flexDirection: "column",
};

const BulkQuoteModal = ({ open, onClose, headerText = "", onProceed, clearCartOnUnmount = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const cartData = useSelector((state) => state.cart.cartData) || [];

  const [search, setSearch] = useState("");
  const [nestedModalOpen, setNestedModalOpen] = useState(false);
  const [designList, setDesignList] = useState([]);
  const [pasteInput, setPasteInput] = useState("");
  const [summaryData, setSummaryData] = useState({});
  const fileInputRef = useRef(null);
  const addBySearchInFlightRef = useRef(false);
  const isBulkMeltingFlow = headerText?.toLowerCase().includes("bulk melting");

  // Calculate summary data whenever cartData changes
  useEffect(() => {
    if (cartData && cartData.length > 0) {
      calculateSummary(cartData);
    } else {
      setSummaryData({
        totalCarat: "0 ct",
        totalWeight: "0 g",
        totalItems: "0",
        totalCategories: "0",
      });
    }
  }, [cartData]);

  const designs = useMemo(() => {
    const uniqueDesigns = [];
    const seenStyleNumbers = new Set();

    cartData?.forEach((item) => {
      if (!seenStyleNumbers.has(item.styleNumber)) {
        seenStyleNumbers.add(item.styleNumber);
        uniqueDesigns.push(item);
      }
    });

    return uniqueDesigns;
  }, [cartData]);

  const handleRemoveItem = (designData) => {
    dispatch(removeFromCart(designData));
  };

  const calculateSummary = useCallback(
    (type = "diamond") => {
      const total = cartData?.reduce((total, item) => {
        let weightStr = "";

        if (type === "diamond") {
          weightStr = item?.diaWt || item?.diaWeight || item?.diamond?.split(" Ct /")[0] || "0";
        } else if (type === "gold") {
          weightStr = item?.netWeight || item?.netWt || item?.weight?.split(" g /")[0] || "0";
        }

        const weight = parseFloat(weightStr) || 0;
        return total + weight;
      }, 0);

      return parseFloat(total.toFixed(4));
    },
    [cartData]
  );
  const categoryList = useMemo(() => {
    if (!cartData) return [];
    const categories = cartData.map((item) => item?.categoryName);
    return [...new Set(categories)];
  }, [cartData]);

  const fetchAndAddDesigns = async (styleNumbers = []) => {
    try {
      const normalizedStyleNumbers = (Array.isArray(styleNumbers) ? styleNumbers : [styleNumbers])
        .map((value) =>
          String(value || "")
            .trim()
            .toUpperCase()
        )
        .filter(Boolean);
      if (!normalizedStyleNumbers.length) return;

      let designs = [];
      if (isBulkMeltingFlow) {
        const responses = await apiClient.get(`/api/v1/stocks/getStockByStyleNumberOrJewelCode`, {
          params: {
            styleNumber: normalizedStyleNumbers,
          },
        });
        designs = responses?.data?.data?.flatMap((item) => item || []).filter(Boolean);
      } else {
        const res = await apiClient.get(`/api/v1/styles/getScanOrSearchDesignData`, {
          params: {
            styleNumber: normalizedStyleNumbers,
          },
        });
        const responseData = res?.data?.data;
        if (!responseData) return;
        designs = Array.isArray(responseData) ? responseData : [responseData];
      }
      if (!designs.length) return;

      designs.forEach((design) => {
        const formattedDesign = isBulkMeltingFlow ? transformStockData(design) : transformDataForCreateOrderPage(design);

        const payload = {
          location: location,
          product: formattedDesign,
          multipleDesigns: true,
        };

        dispatch(addToCart(payload));
      });
    } catch (err) {
      console.error("Error fetching designs:", err);
    }
  };

  const handleAddDesign = async () => {
    if (addBySearchInFlightRef.current) return;
    const trimmed = search.trim();
    if (!trimmed) return;

    addBySearchInFlightRef.current = true;
    setSearch("");
    try {
      await fetchAndAddDesigns([trimmed]);
    } finally {
      addBySearchInFlightRef.current = false;
    }
  };

  const handleSubmitDesignList = async () => {
    await fetchAndAddDesigns(designList);

    setDesignList([]);
    setNestedModalOpen(false);
  };
  const handleNestedModalClose = () => {
    setDesignList([]);
    setPasteInput("");
    setNestedModalOpen(false);
  };

  const parsePasteInput = (text) => {
    const codes = text
      .split(/[\s,]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    if (codes.length) {
      setDesignList((prev) => [...new Set([...prev, ...codes])]);
      setPasteInput("");
    }
  };
  const handleClearCart = () => {
    if (cartData.length === 0) {
      onClose();
      return;
    }
    dispatch(clearCart());
    onClose();
  };
  const handleProceed = () => {
    if (onProceed) {
      onProceed();
      return;
    }

    const state = isBulkMeltingFlow
      ? {
          backPath: location?.pathname || "/Stock",
          clientDetails: false,
          innerChnages: true,
          isMeltingFlow: true,
          isMeltingFlowName: "Create Melting Stock header",
          name: "Cart",
          openCart: true,
          stockSearch: "",
          subStockSearch: "",
        }
      : {
          innerChnages: true,
          backPath: location?.pathname || "/",
          newName: "Cart",
          collection: false,
          categorySearch: "",
          subCatgeorySearch: "",
          openCart: true,
          clientDetails: false,
        };

    navigate("/CartOrder", { state });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const STYLE_REGEX = /^[A-Z]{2,5}-\d+$/i;
      const JEWEL_REGEX = /^\d+[A-Z]\d+$/i;

      const codes = data
        .flatMap((row) => row)
        .map((val) => String(val || "").trim())
        .filter((val) => STYLE_REGEX.test(val) || JEWEL_REGEX.test(val));

      setDesignList((prev) => [...prev, ...codes]);
    };

    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  useEffect(() => {
    return () => {
      if (clearCartOnUnmount) {
        dispatch(clearCart());
      }
    };
  }, [clearCartOnUnmount, dispatch]);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Slide in={open} direction="left" timeout={220} mountOnEnter unmountOnExit>
        <Box
          sx={{
            ...modalStyle,
            width: isBulkMeltingFlow ? "590px" : modalStyle.width,
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ fontSize: "22px", fontWeight: 500 }}>{headerText}</Box>

            <Box
              onClick={handleClearCart}
              sx={{
                cursor: "pointer",
                "&:hover": { opacity: 0.7 },
              }}
            >
              {getIcon("Cross")}
            </Box>
          </Box>

          {/* SUMMARY */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: "10px",
              mb: 2,
            }}
          >
            {[
              { icon: "BlueDiamond", value: calculateSummary("diamond") },
              { icon: "ColorGold", value: calculateSummary("gold") },
              { icon: "PinkRing", value: cartData?.length },
              { icon: "GreenCategoryIcon", value: categoryList?.length },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  background: "#EFEFF2",
                  borderRadius: "10px",
                  py: 1.2,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: 24, height: 24, mb: 0.5 }}>{getIcon(item.icon)}</Box>
                <Box
                  sx={{
                    fontSize: "11px",
                    fontWeight: 400,
                    fontFamily: "Poppins",
                    fontStyle: "regular",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                  {item.value}
                </Box>
              </Box>
            ))}
          </Box>

          {/* SEARCH */}
          <TextField
            fullWidth
            size="small"
            placeholder="Add by Design number or Jewelcode"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddDesign();
              }
            }}
            sx={{
              borderRadius: "4px",
              "& .MuiInputBase-input": {
                color: "#000",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setNestedModalOpen(true)}>
                    <Box>{getIcon("UploadDesign")}</Box>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ height: "1px", background: "#383838", mb: 1 }} />

          {/* LIST */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {designs && designs.length > 0 ? (
              designs?.map((item) => (
                <Box key={item.styleNumber} className={isBulkMeltingFlow ? "stock" : undefined} sx={isBulkMeltingFlow ? { mb: 1 } : { background: "#F3F3F5", borderRadius: "12px", mb: 1 }}>
                  {isBulkMeltingFlow ? (
                    <StockCard data={item} variant="black" removeCart={handleRemoveItem} disableClick />
                  ) : (
                    <QuantityCard designData={item} removeCart={handleRemoveItem} allData={designs} imageModalRightOffset="470px" />
                  )}
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: "center", mt: 4 }}>No designs found</Box>
            )}
          </Box>

          {/* PROCEED - Navigates to CartOrder page */}
          <Box
            onClick={cartData.length > 0 ? handleProceed : undefined}
            sx={{
              mt: 1,
              background: cartData.length > 0 || designList.length > 0 ? "#50C878" : "#7A7A7A",
              color: "#000000",
              borderRadius: "4px",
              textAlign: "center",
              fontSize: "16px",
              fontWeight: 600,
              py: 1,
              cursor: cartData.length > 0 ? "pointer" : "not-allowed",
              "&:hover": {
                background: cartData.length > 0 || designList.length > 0 ? "#45B069" : "#7A7A7A",
              },
              opacity: 1,
            }}
          >
            PROCEED
          </Box>

          {/* Nested Modal - Bottom Right */}
          <Dialog
            open={nestedModalOpen}
            onClose={handleNestedModalClose}
            TransitionComponent={Slide}
            TransitionProps={{ direction: "left", timeout: 600 }}
            PaperProps={{
              sx: {
                position: "absolute",
                bottom: 20,
                right: isBulkMeltingFlow ? 600 : 455,
                m: 0,
                width: "280px",
                borderRadius: "12px",
                background: "#fff",
              },
            }}
            hideBackdrop
          >
            <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#000" }}>Copy + Paste</Typography>
              <IconButton size="small" onClick={handleNestedModalClose}>
                {getIcon("Cross")}
              </IconButton>
            </DialogTitle>

            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1.5, color: "#000" }}>
                Add Design Number
              </Typography>

              <input type="file" accept=".xlsx,.xls,.csv" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />

              <Box
                tabIndex={0}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = e.clipboardData.getData("text");
                  parsePasteInput(text);
                }}
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  minHeight: "80px",
                  maxHeight: "118px",
                  overflowY: "auto",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.8,
                  outline: "none",
                  cursor: "text",
                  "&:focus": { borderColor: "#888" },
                }}
              >
                {designList.length === 0 ? (
                  <Typography sx={{ fontSize: "13px", color: "#aaa", userSelect: "none" }}>
                    Click here and paste Jewel Codes and Style Number
                  </Typography>
                ) : (
                  designList.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        bgcolor: "#f0f0f0",
                        borderRadius: "4px",
                        px: 1.2,
                        py: 0.6,
                      }}
                    >
                      <Typography sx={{ fontSize: "13px", color: "#000" }}>{item}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => setDesignList((prev) => prev.filter((_, i) => i !== index))}
                        sx={{ p: 0.1, "& svg": { fontSize: "6px" } }}
                      >
                        {getIcon("Cross")}
                      </IconButton>
                    </Box>
                  ))
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button
                fullWidth
                variant="contained"
                disabled={designList.length === 0}
                onClick={handleSubmitDesignList}
                sx={{
                  bgcolor: designList.length > 0 ? "#50C878" : "#7A7A7A",
                  color: "#000000 !important",
                  WebkitTextFillColor: "#000000 !important",
                  "&:hover": { bgcolor: designList.length > 0 ? "#45B069" : "#7A7A7A" },
                  "&.MuiButton-contained.Mui-disabled": {
                    bgcolor: "#7A7A7A",
                    color: "#000000 !important",
                    WebkitTextFillColor: "#000000 !important",
                    opacity: 1,
                  },
                  textTransform: "none",
                  borderRadius: "8px",
                  py: 1.5,
                  fontSize: "18px",
                }}
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Slide>
    </Modal>
  );
};

export default BulkQuoteModal;
